import { HelpButton } from '@components/help/HelpButton'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Venue as VenueModel } from '@models/Venue'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getVenues, selectVenues, setVenueQuery } from '@store/VenueReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import type { TableColumnsType } from 'antd'
import { Col, Input } from 'antd'
import { useEffect } from 'react'

const VenuesPage = () => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const venues = useAppSelector(selectVenues())
  const { query, isLoading } = useAppSelector((state) => state.venue)

  const columns: TableColumnsType<VenueModel> = [
    {
      title: 'Name',
      width: 300,
      key: 'name',
      render: (record: VenueModel) => <Venue.Avatar model={record} />,
    },
    {
      title: 'Type',
      render: (record: VenueModel) => <Venue.TypeTag id={record.type} />,
    },
    {
      title: 'Visibility',
      render: (record: VenueModel) => (
        <Venue.VisibilityOverview model={record} />
      ),
    },
    {
      title: 'Booking',
      render: (record: VenueModel) => <Venue.BookingOverview model={record} />,
    },
  ]

  const fetchVenues = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      columnKey: params.sort.columnKey ?? 'name',
      order: params.sort.order ?? 'ascend',
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: params.filters,
      search: params.search,
    }
    dispatch(setVenueQuery(newQuery))
    dispatch(getVenues(newQuery))
  }

  const { handleTableChange, handleFiltersChange, params } =
    useTableUrlSync<VenueModel>({ onParamsChange: fetchVenues })

  useEffect(() => {
    if (isOpen) return

    fetchVenues(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.listVenue}
      pageTitle={{
        title: 'Venue List',
        level: 4,
        badge: { count: query.pagination?.total, overflowCount: 999999 },
      }}
      noWrapWithCol
    >
      <Col span={24}>
        <TableFilters
          initialValues={{
            types: params.filters.types?.map(Number),
            name: params.search.name,
          }}
          onValuesChange={handleFiltersChange}
          left={[
            {
              name: 'types',
              type: 'filter',
              input: (
                <SBAsyncSelect
                  multiple
                  selectProps={{
                    maxTagCount: 'responsive',
                  }}
                  overrideDisplay="select"
                  entity={Entity.venueType}
                  placeholder="Select type"
                  allowClear
                />
              ),
            },
          ]}
          right={[
            {
              name: 'name',
              type: 'search',
              input: <Input placeholder="Search by name" allowClear />,
            },
            {
              type: 'button',
              input: <HelpButton articleId="41" />,
            },
            {
              type: 'button',
              input: (
                <SBRM.Actions
                  actions={[SBRMAction.Create]}
                  entity={Entity.venue}
                />
              ),
            },
          ]}
        />
        <SBTable
          columns={columns}
          dataSource={venues}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
        />
      </Col>
    </Page>
  )
}

export default VenuesPage
